import _ from "lodash";

export interface MessageTemplateAuditEntry {
  email: string;
  event: string;
  time: number;
  data: any;
}

export interface NormalizedTemplateSnapshot {
  name?: string;
  language?: string;
  category?: string;
  parameter_format?: string;
  message_send_ttl_seconds?: number | null;
  components?: any[];
}

export function getTemplateHistoryKey(templateName = ""): string {
  if (!templateName) return "";
  return `whatsapp_message_template:${encodeURIComponent(templateName.trim().toLowerCase())}`;
}

export function parseTemplateAuditLogs(auditLogs: any[]): MessageTemplateAuditEntry[] {
  return _.compact(
    auditLogs.map((log) => {
      try {
        const data = typeof log.data === "string" ? JSON.parse(log.data) : log.data;
        return {
          email: log.user_email,
          event: log.event,
          time: new Date(log.Timestamp || log.date_created).getTime(),
          data,
        };
      } catch (error) {
        return null;
      }
    })
  ).sort((a, b) => b!.time - a!.time);
}

export function getTemplateAuditSnapshot(entry: MessageTemplateAuditEntry): Record<string, any> {
  if (!entry?.data) return {};
  if (entry.data.snapshot) return entry.data.snapshot;
  if (entry.data.deletedTemplates) {
    return {
      deleted: true,
      deletedTemplates: entry.data.deletedTemplates,
    };
  }
  return entry.data;
}

export function formatTemplateHistoryLabel(entry: MessageTemplateAuditEntry): string {
  const action = _.upperFirst(
    entry?.data?.action || entry?.event?.split(":").pop()?.toLowerCase() || "update"
  );
  const language =
    entry?.data?.language ||
    entry?.data?.deletedTemplates
      ?.map((template) => template.language)
      .filter(Boolean)
      .join(", ");

  return language ? `${action} (${language})` : action;
}

export function normalizeTemplateSnapshot(
  template?: Record<string, any>
): NormalizedTemplateSnapshot {
  if (!template) return {};

  return {
    name: template.name,
    language: template.language,
    category: template.category,
    parameter_format: template.parameter_format || "POSITIONAL",
    message_send_ttl_seconds: template.message_send_ttl_seconds || null,
    components: _.cloneDeep(template.components || []),
  };
}

export function getLatestTrackedTemplateSnapshot(
  auditEntries: MessageTemplateAuditEntry[]
): NormalizedTemplateSnapshot | null {
  const latestTrackedEntry = auditEntries.find((entry) => {
    const action = entry?.data?.action;
    return action === "create" || action === "update";
  });

  if (!latestTrackedEntry) return null;

  return normalizeTemplateSnapshot(getTemplateAuditSnapshot(latestTrackedEntry));
}

export function hasTemplateSnapshotMismatch(
  currentTemplate?: Record<string, any>,
  latestTrackedSnapshot?: NormalizedTemplateSnapshot | null
): boolean {
  if (!currentTemplate || !latestTrackedSnapshot) return false;

  const normalizedCurrent = normalizeTemplateSnapshot(currentTemplate);

  return !_.isEqual(normalizedCurrent, latestTrackedSnapshot);
}
